import { useState, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import axios from "axios";
import { apiConfig, getApiToken } from "./config/authConfig";
import Login from "./components/Login";
import IncidentTypeSelector from "./components/IncidentTypeSelector";
import ParameterForm from "./components/ParameterForm";
import QuestionsForm from "./components/QuestionsForm";
import Confirmation from "./components/Confirmation";
import "./App.css";

const STAGES = ["Select Type", "Parameters", "Questions", "Done"];

function Stepper({ stage }) {
  return (
    <div className="stepper">
      {STAGES.slice(0, 3).map((label, i) => {
        const stageNum = i + 1;
        const isDone = stage > stageNum;
        const isActive = stage === stageNum;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", flex: i < 2 ? 1 : 0 }}>
            <div className={`step${isDone ? " done" : isActive ? " active" : ""}`}>
              <div className="step-dot">{isDone ? "✓" : stageNum}</div>
              <span>{label}</span>
            </div>
            {i < 2 && <div className={`step-line${isDone ? " done" : ""}`} />}
          </div>
        );
      })}
    </div>
  );
}

async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();

  const [stage, setStage] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [paramAnswers, setParamAnswers] = useState({});
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [fileMap, setFileMap] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const onFileChange = useCallback((questionId, updater) => {
    setFileMap((prev) => ({ ...prev, [questionId]: updater(prev[questionId] || []) }));
  }, []);

  const resetForm = () => {
    setStage(1);
    setSelectedType(null);
    setParamAnswers({});
    setQuestionAnswers({});
    setFileMap({});
    setSubmitError(null);
    setConfirmation(null);
  };


  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const token = await getApiToken(instance);
      if (!token) return; // redirect in progress — page will reload
      const account = accounts[0];
      const submittedBy = account.username;

      const attachments = [];
      for (const [questionId, files] of Object.entries(fileMap)) {
        for (const file of files) {
          const base64 = await readFileAsBase64(file);
          const question = selectedType.questions.find((q) => q.id === questionId);
          attachments.push({
            questionId,
            questionNum: question?.num,
            originalName: file.name,
            contentType: file.type || "application/octet-stream",
            base64,
          });
        }
      }

      const payload = {
        incidentTypeId: selectedType.id,
        incidentTypeLabel: selectedType.label,
        incidentTypeCode: selectedType.code,
        parameters: paramAnswers,
        questions: questionAnswers,
        attachments,
        submittedBy,
      };

      const { data } = await axios.post(
        `${apiConfig.baseUrl}/api/incident/submit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setConfirmation({ transactionNumber: data.transactionNumber });
      setStage(4);
    } catch (err) {
      setSubmitError(err?.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div className="login-wrap">
        <div className="spinner-wrap"><div className="spinner" /></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Login />;

  const account = accounts[0];

  return (
    <>
      <header className="app-header">
        <span className="app-header-title">📋 Incident Report</span>
        <span className="app-header-user">{account?.name || account?.username}</span>
      </header>

      <main className="app-main">
        {stage < 4 && <Stepper stage={stage} />}

        {stage === 1 && (
          <IncidentTypeSelector
            onSelect={(type) => {
              setSelectedType(type);
              setParamAnswers({});
              setQuestionAnswers({});
              setFileMap({});
              setStage(2);
            }}
          />
        )}

        {stage === 2 && selectedType && (
          <ParameterForm
            incidentType={selectedType}
            answers={paramAnswers}
            onAnswersChange={setParamAnswers}
            onNext={() => setStage(3)}
            onBack={() => setStage(1)}
          />
        )}

        {stage === 3 && selectedType && (
          <QuestionsForm
            incidentType={selectedType}
            answers={questionAnswers}
            onAnswersChange={setQuestionAnswers}
            fileMap={fileMap}
            onFileChange={onFileChange}
            onSubmit={handleSubmit}
            onBack={() => setStage(2)}
            submitting={submitting}
            submitError={submitError}
          />
        )}

        {stage === 4 && confirmation && (
          <Confirmation
            transactionNumber={confirmation.transactionNumber}
            incidentType={selectedType?.label}
            submittedBy={account?.username}
            onReset={resetForm}
          />
        )}
      </main>
    </>
  );
}
